import React, { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { Container, Button, FlexContainer } from '../styles/GlobalStyles';
import { APP_VERSION, ENVIRONMENT } from '../version';
import { BugReportModal } from './BugReportModal';

const Header = styled.header`
  background: var(--card-background);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-md) 0;
  margin-bottom: var(--spacing-xl);
`;

const HeaderContent = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.h1`
  color: var(--primary-color);
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const Navigation = styled.nav`
  display: flex;
  gap: var(--spacing-lg);
  align-items: center;
  
  @media (max-width: 767px) {
    gap: var(--spacing-md);
  }
`;

const NavLink = styled(Link)<{ $active: boolean }>`
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};
  text-decoration: none;
  font-weight: 500;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--primary-color);
    background: var(--primary-color)10;
  }
  
  @media (max-width: 767px) {
    font-size: 0.875rem;
    padding: var(--spacing-xs) var(--spacing-sm);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  
  @media (max-width: 767px) {
    flex-direction: column;
    gap: var(--spacing-sm);
  }
`;

const UserNameLink = styled(Link)`
  color: var(--text-primary);
  font-weight: 500;
  text-decoration: none;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--primary-color)10;
    color: var(--primary-color);
  }
`;

const Main = styled.main`
  min-height: calc(100vh - 180px);
`;

const Footer = styled.footer`
  background: var(--card-background);
  border-top: 1px solid var(--border-color);
  padding: var(--spacing-md) 0;
  margin-top: var(--spacing-xl);
  text-align: center;
`;

const FooterContent = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
`;

const VersionBadge = styled.span`
  background: var(--primary-color)15;
  color: var(--primary-color);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  font-weight: 600;
`;

const BugReportButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);

  &:hover {
    color: var(--primary-color);
    background: var(--primary-color)10;
  }
`;

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // Log version to console on mount
  useEffect(() => {
    console.log(`%c🏷️ App Version: ${APP_VERSION}`, 'color: #1976d2; font-weight: bold; font-size: 14px;');
    console.log(`%c🌍 Environment: ${ENVIRONMENT}`, 'color: #1976d2; font-weight: bold; font-size: 14px;');
  }, []);

  return (
    <>
      <Header>
        <Container>
          <HeaderContent>
            <FlexContainer $align="center" $gap="var(--spacing-xl)">
              <Logo>GamePath</Logo>
              <Navigation>
                <NavLink to="/dashboard" $active={isActive('/dashboard')}>
                  Dashboard
                </NavLink>
                <NavLink to="/reports" $active={isActive('/reports')}>
                  Reports
                </NavLink>
              </Navigation>
            </FlexContainer>
            <UserInfo>
              <UserNameLink to="/profile">
                {user?.profile.firstName} {user?.profile.lastName}
              </UserNameLink>
              <Button $variant="secondary" onClick={logout}>
                Sign Out
              </Button>
            </UserInfo>
          </HeaderContent>
        </Container>
      </Header>
      <Main>
        <Container>{children}</Container>
      </Main>
      <Footer>
        <Container>
          <FooterContent>
            <span>© {new Date().getFullYear()} GamePath</span>
            <span>•</span>
            <VersionBadge>v{APP_VERSION}</VersionBadge>
            {ENVIRONMENT && ENVIRONMENT !== 'production' && (
              <>
                <span>•</span>
                <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  {ENVIRONMENT}
                </span>
              </>
            )}
            <span>•</span>
            <BugReportButton onClick={() => setIsBugReportOpen(true)}>
              🐛 Report a Bug
            </BugReportButton>
          </FooterContent>
        </Container>
      </Footer>
      <BugReportModal 
        isOpen={isBugReportOpen} 
        onClose={() => setIsBugReportOpen(false)} 
      />
    </>
  );
};